import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, message } from "antd";
import intl from "react-intl-universal";
import { createModelConfig, deleteModelConfig, getModelConfigs, getModelProviders, updateModelConfig } from "@/apis/modelConfig";
import { createModelConfigPayload, isValidModelApiKey, requiresModelApiKey } from "./modelConfigUtils";
import styles from "./modelConfig.module.scss";

const t = (key) => intl.get(`ModelConfig.${key}`);

export default function ModelConfig() {
  const [form] = Form.useForm();
  const [providers, setProviders] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const providerCode = Form.useWatch("providerCode", form);
  const selectedProvider = providers.find((provider) => provider.providerCode === providerCode);
  const keyRequired = requiresModelApiKey(editing, providerCode);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [catalog, list] = await Promise.all([getModelProviders(), getModelConfigs()]);
      setProviders(catalog.data || []);
      setConfigs(list.data || []);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (managerOpen) load(); }, [load, managerOpen]);

  const close = () => {
    form.resetFields();
    setOpen(false);
    setEditing(null);
  };
  const edit = (config = null) => {
    form.resetFields();
    setEditing(config);
    form.setFieldsValue(config ? {
      name: config.name, providerCode: config.providerCode, modelId: config.modelId, enabled: config.enabled,
    } : { enabled: true });
    setOpen(true);
  };
  const save = async (values) => {
    setSaving(true);
    try {
      const payload = createModelConfigPayload(values, editing);
      if (editing) await updateModelConfig(editing.id, payload);
      else await createModelConfig(payload);
      message.success(t("Saved"));
      close();
      await load();
    } catch {
      // The shared interceptor displays server errors. Clear secrets after every attempt.
    } finally {
      form.setFieldValue("apiKey", undefined);
      setSaving(false);
    }
  };
  const toggle = async (config, enabled) => {
    setBusyId(config.id);
    try {
      const response = await updateModelConfig(config.id, createModelConfigPayload({ ...config, enabled }, config));
      setConfigs((list) => list.map((item) => item.id === config.id ? response.data : item));
    } catch {
      await load();
    } finally {
      setBusyId(null);
    }
  };
  const remove = (config) => Modal.confirm({
    title: t("Delete confirmation"), content: t("Delete warning"),
    okText: t("Delete"), cancelText: t("Cancel"), okButtonProps: { danger: true },
    async onOk() {
      await deleteModelConfig(config.id);
      setConfigs((list) => list.filter((item) => item.id !== config.id));
      message.success(t("Deleted"));
    },
  });
  const columns = [
    { title: t("Name"), dataIndex: "name", ellipsis: true },
    { title: t("Provider"), dataIndex: "providerCode", render: (code) => providers.find((item) => item.providerCode === code)?.providerName || code },
    { title: t("Model"), dataIndex: "modelId", ellipsis: true },
    { title: "API Key", dataIndex: "apiKeyMasked", render: () => <Tag>********</Tag> },
    { title: t("Enabled"), dataIndex: "enabled", render: (enabled, config) => <Switch checked={enabled} aria-label={`${t("Enabled")} ${config.name}`} loading={busyId === config.id} disabled={Boolean(busyId) || loading || loadFailed} onChange={(value) => toggle(config, value)} /> },
    { title: t("Actions"), render: (_, config) => <Space>
      <Button type="link" disabled={Boolean(busyId) || loading || loadFailed} onClick={() => edit(config)}>{t("Edit")}</Button>
      <Button type="link" danger disabled={Boolean(busyId) || loading || loadFailed} onClick={() => remove(config)}>{t("Delete")}</Button>
    </Space> },
  ];

  return <>
    <Button className={styles.entry} block onClick={() => setManagerOpen(true)}>{t("Title")}</Button>
    <Modal title={t("Title")} open={managerOpen} onCancel={() => setManagerOpen(false)} footer={null} width={960} destroyOnHidden closable={!open && !busyId} maskClosable={!open && !busyId} keyboard={!open && !busyId}>
      <div className={styles.header}>
        <Space>
          <Button onClick={load} disabled={loading || Boolean(busyId)}>{t("Refresh")}</Button>
          <Button type="primary" disabled={loading || loadFailed || !providers.length || Boolean(busyId)} onClick={() => edit()}>{t("Add")}</Button>
        </Space>
      </div>
      <Alert type="info" showIcon title={t("Description")} />
      {loadFailed && <Alert type="error" showIcon title={t("Load failed")} action={<Button onClick={load}>{t("Retry")}</Button>} />}
      <Table className={styles.table} rowKey="id" columns={columns} dataSource={configs} loading={loading} scroll={{ x: 760 }} pagination={{ pageSize: 5 }} locale={{ emptyText: t("Empty") }} />
    </Modal>
    <Modal title={t(editing ? "Edit" : "Add")} open={open} onCancel={close} footer={null} destroyOnHidden closable={!saving} maskClosable={!saving} keyboard={!saving}>
      <Form form={form} layout="vertical" onFinish={save} preserve={false} autoComplete="off" disabled={saving}>
        <Form.Item name="name" label={t("Name")} rules={[{ required: true, whitespace: true, message: t("Name required") }]}>
          <Input maxLength={128} />
        </Form.Item>
        <Form.Item name="providerCode" label={t("Provider")} rules={[{ required: true, message: t("Provider required") }, { validator: (_, value) => !value || providers.some((item) => item.providerCode === value) ? Promise.resolve() : Promise.reject(new Error(t("Provider required"))) }]}>
          <Select options={providers.map((provider) => ({ value: provider.providerCode, label: provider.providerName }))} onChange={() => form.setFieldsValue({ modelId: undefined, apiKey: undefined })} />
        </Form.Item>
        <Form.Item name="modelId" label={t("Model")} rules={[{ required: true, message: t("Model required") }, { validator: (_, value) => !value || selectedProvider?.models?.some((model) => model.modelId === value) ? Promise.resolve() : Promise.reject(new Error(t("Model required"))) }]}>
          <Select disabled={!selectedProvider} options={(selectedProvider?.models || []).map((model) => ({ value: model.modelId, label: `${model.name || model.modelId} (${model.modelId})` }))} />
        </Form.Item>
        <Form.Item name="apiKey" label="API Key" extra={t(keyRequired ? "Key required hint" : "Keep key hint")} rules={[
          { required: keyRequired, message: t("Key required") },
          { validator: (_, value) => !value || isValidModelApiKey(value) ? Promise.resolve() : Promise.reject(new Error(t("Key invalid"))) },
        ]}>
          <Input.Password maxLength={8192} autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="enabled" label={t("Enabled")} valuePropName="checked"><Switch /></Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>{t("Save")}</Button>
          <Button disabled={saving} onClick={close}>{t("Cancel")}</Button>
        </Space>
      </Form>
    </Modal>
  </>;
}
